import { Link } from "react-router";
import type { BlockContentMap } from "@veo/types";
import { cn } from "@veo/ui";
import { asPosts } from "../lib/section-data";
import { ArticleCard } from "../components/ArticleCard";
import { RevealItem, RevealList } from "../components/motion";
import {
  SectionHeading,
  SectionShell,
  isDarkBackground,
  type SectionProps,
} from "./SectionRenderer";

const COLUMNS: Record<number, string> = {
  1: "",
  2: "md:grid-cols-2",
  3: "md:grid-cols-2 lg:grid-cols-3",
};

export default function ArticleListingBlock({
  content,
  settings,
  data,
}: SectionProps<BlockContentMap["articleListing"]>) {
  const onDark = isDarkBackground(settings);
  const posts = asPosts(data) ?? [];

  // An empty insights section on a live page looks broken, so it renders
  // nothing at all rather than a heading above a gap.
  if (posts.length === 0) return null;

  return (
    <SectionShell settings={settings}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex-1">
          <SectionHeading
            eyebrow={content.eyebrow}
            heading={content.heading}
            intro={content.intro}
            settings={{ ...settings, alignment: "left" }}
          />
        </div>
        {content.viewAllButton?.href && (
          <Link
            to={content.viewAllButton.href}
            className={cn(
              "mb-12 text-sm font-semibold underline decoration-gold-500 underline-offset-4",
              onDark ? "text-gold-300" : "text-navy-700",
            )}
          >
            {content.viewAllButton.label ?? "All insights"}
          </Link>
        )}
      </div>

      <RevealList className={cn("grid gap-10", COLUMNS[content.columns] ?? COLUMNS[3])}>
        {posts.slice(0, content.limit).map((post) => (
          <RevealItem key={post.id}>
            <ArticleCard
              post={post}
              showExcerpt={content.showExcerpt}
              showAuthor={content.showAuthor}
              showDate={content.showDate}
              showReadingTime={content.showReadingTime}
              onDark={onDark}
              sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
            />
          </RevealItem>
        ))}
      </RevealList>
    </SectionShell>
  );
}
